// Given the current pathname, return the equivalent path in every locale for
// the language switcher. Covers static pages (slugMap) and dynamic supporter
// profiles (supporters data). When a page/profile does not yet exist in a
// target locale (phased rollout), that locale falls back to its localized home
// page so the switcher never produces a dead link.

import { locales, type Locale } from "./config";
import { hasLocalizedPath, pageIdFromPath, pathFor } from "./slugMap";
import { findSupporterByPath, isTranslated, supporterPath } from "@/data/supporters";
import { articlePath, findPostByPath, hasContent } from "@/data/blog/posts";
import { findTermByPath, termPath } from "@/data/ordbog";

export function alternatesForPath(pathname: string): Record<Locale, string> {
  const home = (l: Locale) => pathFor("home", l);

  const id = pageIdFromPath(pathname);
  if (id) {
    return Object.fromEntries(
      locales.map((l) => [l, hasLocalizedPath(id, l) ? pathFor(id, l) : home(l)]),
    ) as Record<Locale, string>;
  }

  const match = findSupporterByPath(pathname);
  if (match) {
    return Object.fromEntries(
      locales.map((l) => [l, isTranslated(match.supporter, l) ? supporterPath(match.supporter, l) : home(l)]),
    ) as Record<Locale, string>;
  }

  const postMatch = findPostByPath(pathname);
  if (postMatch) {
    return Object.fromEntries(
      locales.map((l) => [
        l,
        hasContent(postMatch.post, l) ? articlePath(postMatch.post, l) : pathFor("artikler", l),
      ]),
    ) as Record<Locale, string>;
  }

  const term = findTermByPath(pathname);
  if (term) {
    // Terms are Danish-only for now; other locales fall back to the listing.
    return Object.fromEntries(
      locales.map((l) => [l, l === "da" ? termPath(term, "da") : pathFor("viden", l)]),
    ) as Record<Locale, string>;
  }

  return Object.fromEntries(locales.map((l) => [l, home(l)])) as Record<Locale, string>;
}
